import _ from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { SportsbookViewer } from '@/components/sportsbook/SportsbookViewer'
import { SportsbookViewerSkeleton } from '@/components/sportsbook/SportsbookViewerSkeleton'
import { PageShell } from '@/components/layout/PageShell'
import { Button } from '@/components/ui/button'
import { useDefaultCurrencyId } from '@/hooks/use-default-currency'
import { useGetSportsbooksQuery, useLazyLaunchSportsbookQuery } from '@/store/api/sportsbookApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setActiveSection } from '@/store/slices/configSlice'
import { selectSelectedSportsbookId, setSelectedSportsbook } from '@/store/slices/sportsbookSlice'

interface SportsbookLaunchData {
	url: string
	token: string
	sessionId: string
}

export default function Sportsbook() {
	const dispatch = useAppDispatch()
	const selectedSportsbookId = useAppSelector(selectSelectedSportsbookId)
	const defaultCurrencyId = useDefaultCurrencyId()
	const { data: sportsbooksResponse, isLoading: isSportsbooksLoading, isError: isSportsbooksError } = useGetSportsbooksQuery()
	const [launchSportsbook, { isFetching: isLaunching }] = useLazyLaunchSportsbookQuery()
	const [launchData, setLaunchData] = useState<SportsbookLaunchData | null>(null)
	const [launchError, setLaunchError] = useState(false)

	const sportsbooks = sportsbooksResponse?.data ?? []
	const selectedSportsbook = useMemo(() => _.find(sportsbooks, { id: selectedSportsbookId }) ?? sportsbooks[0] ?? null, [sportsbooks, selectedSportsbookId])

	useEffect(() => {
		dispatch(setActiveSection('sport'))
	}, [dispatch])

	useEffect(() => {
		if (sportsbooks.length === 0 || selectedSportsbookId) {
			return
		}

		dispatch(setSelectedSportsbook(sportsbooks[0].id))
	}, [dispatch, selectedSportsbookId, sportsbooks])

	const launchSelectedSportsbook = useCallback(
		async (sportsbookId: string) => {
			if (!defaultCurrencyId) {
				return
			}

			setLaunchError(false)
			const response = await launchSportsbook({
				sportsbook_id: sportsbookId,
				currency_id: defaultCurrencyId,
			})

			const data = 'data' in response ? response.data?.data : null

			if (data?.token && data.session_id) {
				setLaunchData({
					url: data.url,
					token: data.token,
					sessionId: data.session_id,
				})
				return
			}

			setLaunchData(null)
			setLaunchError(true)
		},
		[defaultCurrencyId, launchSportsbook],
	)

	useEffect(() => {
		if (!selectedSportsbookId || !defaultCurrencyId) {
			return
		}

		setLaunchData(null)
		void launchSelectedSportsbook(selectedSportsbookId)
	}, [defaultCurrencyId, launchSelectedSportsbook, selectedSportsbookId])

	if (isSportsbooksLoading) {
		return (
			<div className='flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden'>
				<SportsbookViewerSkeleton />
			</div>
		)
	}

	if (isSportsbooksError) {
		return (
			<PageShell centered className='min-h-[50vh] justify-center py-10'>
				<p className='text-sm text-muted-foreground'>Unable to load sportsbooks right now.</p>
			</PageShell>
		)
	}

	if (_.isEmpty(sportsbooks)) {
		return (
			<PageShell centered className='min-h-[50vh] justify-center py-10'>
				<p className='text-sm text-muted-foreground'>No sportsbooks are available right now.</p>
			</PageShell>
		)
	}

	if (launchError && !launchData) {
		return (
			<PageShell centered className='min-h-[50vh] justify-center gap-3 py-10'>
				<p className='text-sm text-muted-foreground'>Unable to launch this sportsbook right now.</p>
				<Button
					type='button'
					variant='primary'
					onClick={() => selectedSportsbookId && void launchSelectedSportsbook(selectedSportsbookId)}
				>
					Try again
				</Button>
			</PageShell>
		)
	}

	return (
		<div className='flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden'>
			<SportsbookViewer
				url={launchData?.url ?? null}
				sportsbookName={selectedSportsbook?.sportsbook_name}
				token={launchData?.token ?? null}
				sessionId={launchData?.sessionId ?? null}
				isLaunching={isLaunching}
			/>
		</div>
	)
}
